<template>
    <Form v-slot="$form" :initialValues="initialValues" :resolver="resolver" @submit="handleSubmit" ref="formRef">
        <div class="space-y-4">
            <!-- Name Field -->
            <div>
                <FloatLabel variant="in" fluid>
                    <InputText id="name" name="name" fluid type="text" />
                    <label for="name">{{ $t("name") }}*</label>
                </FloatLabel>
                <Message v-if="$form.name?.invalid" severity="error" size="small" variant="simple">
                    {{ $form.name.error?.message }}
                </Message>
            </div>

            <!-- Submit Buttons -->
            <div class="flex gap-2 pt-2">
                <Button type="button" :label="$t('cancel')" severity="secondary" outlined @click="$emit('cancel')"
                    class="flex-1" />
                <Button type="submit" :label="$t('save')" icon="pi pi-save" iconPos="right" :disabled="submitDisabled"
                    :loading="isSubmitting" class="flex-1" />
            </div>
        </div>
    </Form>
</template>

<script setup lang="ts">
import { ref, computed, useTemplateRef, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useChoreSettingsStore } from '@/stores';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { Form, type FormSubmitEvent } from '@primevue/forms';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import type { CategoryDto, CreateCategoryDto } from '@/client';

const { t } = useI18n();
const toast = useToast();
const formRef = useTemplateRef('formRef');

const props = defineProps<{
    category?: CategoryDto | null;
}>();

const emit = defineEmits<{
    (e: 'submitted'): void;
    (e: 'cancel'): void;
}>();

// Stores
const choreSettingsStore = useChoreSettingsStore();
const { createCategory } = choreSettingsStore;
const { categories } = storeToRefs(choreSettingsStore);

// Form state
const isSubmitting = ref(false);

// Form validation
const initialValues = ref({
    name: '',
});

const resolver = computed(() => {
    return zodResolver(
        z.object({
            name: z.string()
                .min(1, { message: t('validation.nameRequired') })
                .min(2, { message: t('validation.categoryNameMinLength') })
                .max(50, { message: t('validation.categoryNameMaxLength') })
                .refine((name) => {
                    // Skip duplicate check if editing the same category
                    if (props.category && name.toLowerCase() === props.category.name?.toLowerCase()) {
                        return true;
                    }
                    // Check for duplicates among existing categories
                    return !categories.value?.some(category =>
                        category.name?.toLowerCase() === name.toLowerCase() &&
                        category.id !== props.category?.id
                    );
                }, { message: t('validation.categoryNameDuplicate') })
        })
    );
});

// Computed properties
const submitDisabled = computed(() => {
    return isSubmitting.value;
});

// Watch for category changes to populate form
watch(() => props.category, (newCategory) => {
    if (newCategory) {
        initialValues.value = {
            name: newCategory.name || '',
        };
        // Reset the form with new values
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (formRef.value as any)?.reset();
    } else {
        resetForm();
    }
}, { immediate: true });

// Methods
async function handleSubmit(e: FormSubmitEvent<Record<string, any>>) {
    if (!e.valid) {
        console.error("Form validation failed:", e.errors);
        return;
    }

    if (submitDisabled.value) {
        return;
    }

    isSubmitting.value = true;
    try {
        if (props.category) {
            // Update functionality would go here if needed
            // For now, we only support create
            console.warn("Category update not implemented");
        } else {
            // Create new category
            const createDto: CreateCategoryDto = {
                name: e.values.name,
            };

            await createCategory(createDto);
        }

        // Reset form and emit success
        if (!props.category) {
            resetForm();
        }
        emit('submitted');
    } catch (error) {
        console.error("Error while saving category:", error);
        toast.add({
            severity: 'error',
            summary: t('error'),
            detail: props.category ? t('settings.categories.updateError') : t('settings.categories.createError'),
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
}

function resetForm() {
    Object.assign(initialValues.value, {
        name: '',
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (formRef.value as any)?.reset();
}
</script>